import { Typography } from '@material-ui/core';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { CtaButton } from '../CtaButton';
import { Headline } from '../Headline';
import { LogosSection } from '../LogosSection';
import { Reveal } from '../motion';
import { AgenticMontage } from './AgenticMontage';
import { HeroGradientBackground } from './GradientBackground';

const HeroSectionWrapper = styled('section')`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const HeroContentWrapper = styled('div')`
  position: relative;
  overflow: hidden;
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 620px;
  padding: 80px 0 112px;

  @media (max-width: 960px) {
    min-height: auto;
    padding: 60px 0 72px;
  }
`;

const HeroWrapper = styled('div')`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 48px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const LeftContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Description = styled(Typography)`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin: 0;

  strong {
    color: #0e1c2b;
  }
` as typeof Typography;

const ButtonGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LearnMoreLink = styled('a')`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const RightContent = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeroSection = ({
  onGetStarted,
  showLogos = false
}: {
  onGetStarted: () => void;
  showLogos?: boolean;
}) => {
  return (
    <HeroSectionWrapper>
      <HeroContentWrapper>
        <HeroGradientBackground />
        <HeroWrapper>
          <LeftContent>
            <Reveal>
              <Headline component="h1">
                Your content is valuable inventory. Make every page earn what
                it's worth.
              </Headline>
            </Reveal>

            <Reveal delay={200}>
              <Description component="p">
                AI-powered commerce infrastructure: one platform to unify
                affiliate data, optimize performance, launch sponsorship
                campaigns, and maximize yield from every placement.
              </Description>
            </Reveal>

            <Reveal delay={300}>
              <ButtonGroup>
                <CtaButton size="medium" onClick={onGetStarted}>
                  Get a demo
                </CtaButton>
                <LearnMoreLink href="/product/amplify/">
                  Explore sponsorship campaigns <ArrowRight size={18} />
                </LearnMoreLink>
              </ButtonGroup>
            </Reveal>
          </LeftContent>

          <RightContent>
            <Reveal delay={250} distance={32}>
              <AgenticMontage />
            </Reveal>
          </RightContent>
        </HeroWrapper>
      </HeroContentWrapper>
      {showLogos ? <LogosSection variant="hero" /> : null}
    </HeroSectionWrapper>
  );
};
